import { initializeApp } from 'firebase/app'
import { 
  getAuth, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut, 
  updateProfile, 
  updateEmail, 
  updatePassword, 
  onAuthStateChanged 
} from 'firebase/auth'
import { 
  getFirestore, 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  setDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  onSnapshot 
} from 'firebase/firestore'


// Firebase Configuration (provided by the user)
const firebaseConfig = {
  apiKey: "AIzaSyAfqjGUYv5Qq029y2yXuJqIyjLqwjTL_dY",
  authDomain: "judgeme-4544f.firebaseapp.com",
  projectId: "judgeme-4544f",
  storageBucket: "judgeme-4544f.firebasestorage.app",
  messagingSenderId: "625188600726",
  appId: "1:625188600726:web:77248ea8419b0f9590bbb7",
  measurementId: "G-5ZB6HB873X"
}

// Initialize Firebase
const app = initializeApp(firebaseConfig)
const auth = getAuth(app)
const db = getFirestore(app)

// Database table names
export const TABLES = {
  EVENTS: 'events',
  CANDIDATES: 'candidates',
  JUDGES: 'judges',
  CRITERIA: 'criteria',
  SCORES: 'scores',
  SPECIAL_AWARDS: 'special_awards',
  SPECIAL_AWARD_VOTES: 'special_award_votes',
  SPECIAL_AWARD_CATEGORIES: 'special_award_categories',
  ACTIVITY_LOGS: 'event_activity_logs',
  ADMIN_CRITERIA_SCORES: 'admin_criteria_scores'
}

// Local cache to temporarily store Base64 data URLs by file path during event creation
const logoBase64Cache = new Map()

// Helper to convert File/Blob to Base64 Data URL
const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve(reader.result)
    reader.onerror = (error) => reject(error)
  })
}

// Map Firebase User to match the Supabase User structure
const mapFirebaseUser = (firebaseUser) => {
  if (!firebaseUser) return null
  return {
    id: firebaseUser.uid,
    uid: firebaseUser.uid,
    email: firebaseUser.email,
    user_metadata: {
      full_name: firebaseUser.displayName || ''
    }
  }
}

// RFC4122 v4 compliant UUID generator for Firestore document IDs (to match Supabase IDs)
function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : ((r & 0x3) | 0x8)
    return v.toString(16)
  })
}

// Parse select columns to handle relation queries (e.g. nested lists like criteria(id, name))
function parseSelectFields(selectStr) {
  const fields = []
  let depth = 0
  let current = ''
  for (let i = 0; i < selectStr.length; i++) {
    const char = selectStr[i]
    if (char === '(') depth++
    else if (char === ')') depth--
    
    if (char === ',' && depth === 0) {
      fields.push(current.trim())
      current = ''
    } else {
      current += char
    }
  }
  if (current.trim()) {
    fields.push(current.trim())
  }
  return fields
}

// Foreign Key Mapping for belongs-to relations
const FOREIGN_KEY_MAP = {
  judges: {
    events: 'event_id'
  },
  score_change_requests: {
    judges: 'judge_id',
    criteria: 'criteria_id',
    special_awards: 'special_award_id'
  },
  help_requests: {
    judges: 'judge_id'
  },
  special_award_votes: {
    special_awards: 'special_award_id',
    judges: 'judge_id',
    candidates: 'candidate_id'
  },
  special_awards: {
    candidates: 'assigned_candidate_id'
  }
}

// Resolve relations in select queries
async function resolveRelation(parentDocs, parentCollection, relationCollection, alias, subFields) {
  if (parentDocs.length === 0) return

  // 1. Resolve children documents
  let resolvedChildDocs = []

  // 1. Has-Many Relations: parent = events
  const hasManyCollections = ['criteria', 'candidates', 'judges', 'scores', 'special_awards', 'event_activity_logs', 'special_award_categories', 'admin_criteria_scores']
  if (parentCollection === 'events' && hasManyCollections.includes(relationCollection)) {
    for (const parentDoc of parentDocs) {
      const q = query(collection(db, relationCollection), where('event_id', '==', parentDoc.id))
      const snap = await getDocs(q)
      const children = snap.docs.map(d => ({ ...d.data(), id: d.id }))
      
      // Apply default orderings for sub-entities in JS to match Supabase schema
      if (relationCollection === 'criteria') {
        children.sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0) || (a.created_at || '').localeCompare(b.created_at || ''))
      } else if (relationCollection === 'candidates') {
        children.sort((a, b) => (a.candidate_number ?? 0) - (b.candidate_number ?? 0))
      } else if (relationCollection === 'special_awards') {
        children.sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0) || (a.created_at || '').localeCompare(b.created_at || ''))
      }
      
      parentDoc[alias] = children
      resolvedChildDocs.push(...children)
    }
  } else {
    // 2. Belongs-To Relations
    const fkField = FOREIGN_KEY_MAP[parentCollection]?.[relationCollection]
    if (fkField) {
      for (const parentDoc of parentDocs) {
        const idVal = parentDoc[fkField]
        if (idVal) {
          const docRef = doc(db, relationCollection, idVal)
          const snap = await getDoc(docRef)
          if (snap.exists()) {
            const childDoc = { ...snap.data(), id: snap.id }
            parentDoc[alias] = childDoc
            resolvedChildDocs.push(childDoc)
          } else {
            parentDoc[alias] = null
          }
        } else {
          parentDoc[alias] = null
        }
      }
    }
  }

  // 3. Recursively resolve nested relations in subFields
  if (resolvedChildDocs.length > 0 && subFields && subFields !== '*') {
    const parsedFields = parseSelectFields(subFields)
    for (const field of parsedFields) {
      if (field.includes('(')) {
        const match = field.match(/^([\w_!:]+)\s*\((.*)\)$/s)
        if (match) {
          const rawRelation = match[1].trim()
          const nestedSubFields = match[2].trim()
          const cleanRelation = rawRelation.replace('!inner', '').replace('!left', '')
          
          let subAlias = cleanRelation
          let subRelationCollection = cleanRelation
          if (cleanRelation.includes(':')) {
            const parts = cleanRelation.split(':')
            subAlias = parts[0].trim()
            subRelationCollection = parts[1].trim()
          }
          
          // Recursively resolve
          await resolveRelation(resolvedChildDocs, relationCollection, subRelationCollection, subAlias, nestedSubFields)
        }
      }
    }
  }
}

// Firebase Query Builder to mimic Supabase query chains (completely synchronous builders)
class FirebaseQueryBuilder {
  constructor(collectionName) {
    this.collectionName = collectionName
    this.filters = []
    this.orderByFields = []
    this.limitVal = null
    this.isSingle = false
    this.results = null
    this.isCountOnly = false
    this.selectColumns = '*'
    this.selectOptions = {}
    this.writeAction = null
    this.writeOptions = null
  }

  eq(field, value) {
    this.filters.push({ field, op: '==', value })
    return this
  }

  in(field, values) {
    if (!values || values.length === 0) {
      this.filters.push({ field, op: 'in', value: ['__empty_list__'] })
    } else {
      this.filters.push({ field, op: 'in', value: values })
    }
    return this
  }

  contains(field, value) {
    const queryVal = Array.isArray(value) ? (value.length > 0 ? value[0] : null) : value
    if (queryVal !== null) {
      this.filters.push({ field, op: 'array-contains', value: queryVal })
    }
    return this
  }

  order(field, options = {}) {
    this.orderByFields.push({
      field,
      direction: options.ascending ? 'asc' : 'desc'
    })
    return this
  }

  limit(val) {
    this.limitVal = val
    return this
  }

  single() {
    this.isSingle = true
    return this
  }

  select(columns = '*', options = {}) {
    this.selectColumns = columns
    this.selectOptions = options
    if (options.count === 'exact' || options.head === true) {
      this.isCountOnly = true
    }
    return this
  }

  insert(data) {
    this.writeAction = { type: 'insert', data }
    return this
  }

  update(data) {
    this.writeAction = { type: 'update', data }
    return this
  }

  delete() {
    this.writeAction = { type: 'delete' }
    return this
  }

  upsert(data, options = {}) {
    this.writeAction = { type: 'upsert', data }
    this.writeOptions = options
    return this
  }

  async execute() {
    // If results have already been fetched/computed, return them
    if (this.results !== null) {
      const data = this.isSingle
        ? (this.results && this.results.length > 0 ? this.results[0] : null)
        : this.results
      let error = null
      if (this.isSingle && !data) {
        error = { code: 'PGRST116', message: 'No rows returned for single query' }
      }
      return { data, error, count: this.isCountOnly ? this.results.length : undefined }
    }

    try {
      if (this.writeAction) {
        const { type, data } = this.writeAction
        
        if (type === 'insert') {
          const list = Array.isArray(data) ? data : [data]
          const results = []
          for (const item of list) {
            const id = item.id || generateUUID()
            const docRef = doc(db, this.collectionName, id)
            const finalData = { ...item, id }
            await setDoc(docRef, finalData)
            results.push(finalData)
          }
          this.results = results
        } else if (type === 'update') {
          let q = collection(db, this.collectionName)
          for (const f of this.filters) {
            q = query(q, where(f.field, f.op, f.value))
          }
          const snap = await getDocs(q)
          const results = []
          for (const d of snap.docs) {
            const docRef = doc(db, this.collectionName, d.id)
            await updateDoc(docRef, data)
            results.push({ ...d.data(), ...data, id: d.id })
          }
          this.results = results
        } else if (type === 'delete') {
          let q = collection(db, this.collectionName)
          for (const f of this.filters) {
            q = query(q, where(f.field, f.op, f.value))
          }
          const snap = await getDocs(q)
          const results = []
          for (const d of snap.docs) {
            const docRef = doc(db, this.collectionName, d.id)
            await deleteDoc(docRef)
            results.push({ ...d.data(), id: d.id })
          }
          this.results = results
        } else if (type === 'upsert') {
          const list = Array.isArray(data) ? data : [data]
          const results = []
          for (const item of list) {
            let docRef = null
            let existingDoc = null
            
            if (item.id) {
              docRef = doc(db, this.collectionName, item.id)
              const snap = await getDoc(docRef)
              if (snap.exists()) {
                existingDoc = snap.data()
              }
            }
            
            if (!existingDoc && this.writeOptions?.onConflict) {
              const conflictFields = this.writeOptions.onConflict.split(',').map(f => f.trim())
              let q = query(collection(db, this.collectionName))
              conflictFields.forEach(field => {
                if (item[field] !== undefined) {
                  q = query(q, where(field, '==', item[field]))
                }
              })
              const snap = await getDocs(q)
              if (!snap.empty) {
                docRef = doc(db, this.collectionName, snap.docs[0].id)
                existingDoc = snap.docs[0].data()
              }
            }
            
            if (!docRef) {
              const id = generateUUID()
              docRef = doc(db, this.collectionName, id)
            }
            
            const id = docRef.id
            const finalData = { ...item, id }
            await setDoc(docRef, finalData)
            results.push(finalData)
          }
          this.results = results
        }
      } else {
        // Read Action (select)
        let q = collection(db, this.collectionName)

        // Handle dotted filters for joins: e.g. special_awards.event_id in special_award_votes query
        let specialAwardsEventIdFilter = null
        const activeFilters = []
        for (const f of this.filters) {
          if (f.field === 'special_awards.event_id') {
            specialAwardsEventIdFilter = f.value
          } else {
            activeFilters.push(f)
          }
        }

        if (specialAwardsEventIdFilter !== null) {
          const saQuery = query(collection(db, 'special_awards'), where('event_id', '==', specialAwardsEventIdFilter))
          const saSnap = await getDocs(saQuery)
          const saIds = saSnap.docs.map(d => d.id)
          if (saIds.length === 0) {
            activeFilters.push({ field: 'special_award_id', op: '==', value: '__none__' })
          } else {
            activeFilters.push({ field: 'special_award_id', op: 'in', value: saIds })
          }
        }

        // Apply where filters
        let fQuery = q
        for (const f of activeFilters) {
          fQuery = query(fQuery, where(f.field, f.op, f.value))
        }

        // Fetch documents matching filters (without orderBy or limit to avoid composite index errors)
        const snap = await getDocs(fQuery)

        if (this.isCountOnly) {
          this.results = snap.docs.map(doc => ({ ...doc.data(), id: doc.id }))
          return { data: null, error: null, count: snap.size }
        }

        let docs = snap.docs.map(doc => ({ ...doc.data(), id: doc.id }))

        // Apply orderBy sorting in memory
        if (this.orderByFields.length > 0) {
          docs.sort((a, b) => {
            for (const ord of this.orderByFields) {
              const aVal = a[ord.field]
              const bVal = b[ord.field]
              
              if (aVal === undefined && bVal === undefined) continue
              if (aVal === undefined) return ord.direction === 'asc' ? 1 : -1
              if (bVal === undefined) return ord.direction === 'asc' ? -1 : 1
              
              if (aVal < bVal) return ord.direction === 'asc' ? -1 : 1
              if (aVal > bVal) return ord.direction === 'asc' ? 1 : -1
            }
            return 0
          })
        }

        // Apply limit slicing in memory
        if (this.limitVal !== null) {
          docs = docs.slice(0, this.limitVal)
        }

        // Resolve relations
        const columns = this.selectColumns
        if (columns && columns !== '*') {
          const parsedFields = parseSelectFields(columns)
          for (const field of parsedFields) {
            if (field.includes('(')) {
              const match = field.match(/^([\w_!:]+)\s*\((.*)\)$/s)
              if (match) {
                const rawRelation = match[1].trim()
                const subFields = match[2].trim()
                const cleanRelation = rawRelation.replace('!inner', '').replace('!left', '')
                
                let alias = cleanRelation
                let relationCollection = cleanRelation
                if (cleanRelation.includes(':')) {
                  const parts = cleanRelation.split(':')
                  alias = parts[0].trim()
                  relationCollection = parts[1].trim()
                }
                
                await resolveRelation(docs, this.collectionName, relationCollection, alias, subFields)
              }
            }
          }
        }

        this.results = docs
      }

      const data = this.isSingle
        ? (this.results && this.results.length > 0 ? this.results[0] : null)
        : this.results
      
      let error = null
      if (this.isSingle && !data) {
        error = { code: 'PGRST116', message: 'No rows returned for single query' }
      }
      
      return { data, error, count: this.isCountOnly ? this.results.length : undefined }
    } catch (err) {
      console.error('Firestore operation error:', err)
      return { data: null, error: err }
    }
  }

  then(onFulfilled, onRejected) {
    return this.execute().then(onFulfilled, onRejected)
  }
}

// Real-time channel listener simulation using Firestore onSnapshot
class FirebaseChannel {
  constructor(name) {
    this.name = name
    this.listeners = []
    this.unsubscribes = []
  }

  on(event, filter, callback) {
    this.listeners.push({ event, filter, callback })
    return this
  }

  subscribe(statusCallback) {
    setTimeout(() => {
      if (statusCallback) statusCallback('SUBSCRIBED')
    }, 0)

    this.listeners.forEach(({ filter, callback }) => {
      const collectionName = filter.table
      const q = query(collection(db, collectionName))
      
      // Skip the initial onSnapshot load to trigger only on subsequent modifications (inserts/updates/deletes)
      let isFirst = true
      const unsub = onSnapshot(q, (snapshot) => {
        if (isFirst) {
          isFirst = false
          return
        }
        callback({ new: {} })
      }, (err) => {
        console.error(`Realtime error for table ${collectionName}:`, err)
      })
      this.unsubscribes.push(unsub)
    })

    return this
  }

  unsubscribe() {
    this.unsubscribes.forEach(unsub => unsub())
    this.unsubscribes = []
  }
}

// Exported Supabase Mock client object
export const supabase = {
  auth: {
    getSession: async () => {
      const user = auth.currentUser
      return { 
        data: { 
          session: user ? { user: mapFirebaseUser(user) } : null 
        }, 
        error: null 
      }
    },
    
    onAuthStateChange: (callback) => {
      const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
        const session = firebaseUser ? { user: mapFirebaseUser(firebaseUser) } : null
        callback('SIGNED_IN', session)
      })
      return { data: { subscription: { unsubscribe } } }
    },
    
    signUp: async ({ email, password, options }) => {
      try {
        const credential = await createUserWithEmailAndPassword(auth, email, password)
        const fullName = options?.data?.full_name || ''
        if (fullName) {
          await updateProfile(credential.user, { displayName: fullName })
        }
        
        // Write to user_profiles collection so the user can be searched by ID or Email
        const userId = credential.user.uid
        await setDoc(doc(db, 'user_profiles', userId), {
          id: userId,
          email: email.toLowerCase(),
          full_name: fullName,
          created_at: new Date().toISOString()
        })

        const mappedUser = mapFirebaseUser(credential.user)
        if (fullName) {
          mappedUser.user_metadata.full_name = fullName
        }
        return { data: { user: mappedUser }, error: null }
      } catch (err) {
        return { data: { user: null }, error: err }
      }
    },
    
    signInWithPassword: async ({ email, password }) => {
      try {
        const credential = await signInWithEmailAndPassword(auth, email, password)
        const user = mapFirebaseUser(credential.user)
        return { data: { user, session: { user } }, error: null }
      } catch (err) {
        return { data: { user: null, session: null }, error: err }
      }
    },
    
    signOut: async () => {
      try {
        await signOut(auth)
        return { error: null }
      } catch (err) {
        return { error: err }
      }
    },
    
    updateUser: async (attributes) => {
      try {
        const user = auth.currentUser
        if (!user) throw new Error("No authenticated user")
        
        if (attributes.password) {
          await updatePassword(user, attributes.password)
        }
        if (attributes.email) {
          await updateEmail(user, attributes.email)
        }
        if (attributes.data && attributes.data.full_name) {
          await updateProfile(user, { displayName: attributes.data.full_name })
        }
        
        const mapped = mapFirebaseUser(auth.currentUser)
        return { data: { user: mapped }, error: null }
      } catch (err) {
        return { data: { user: null }, error: err }
      }
    }
  },
  
  from: (collectionName) => {
    return new FirebaseQueryBuilder(collectionName)
  },
  
  channel: (name) => {
    return new FirebaseChannel(name)
  },
  
  rpc: async (name, args) => {
    try {
      if (name === 'get_user_email_by_id') {
        const userId = args.user_uuid
        const docRef = doc(db, 'user_profiles', userId)
        const snap = await getDoc(docRef)
        if (snap.exists()) {
          return { data: [{ email_address: snap.data().email }], error: null }
        }
        return { data: [], error: null }
      }
      
      if (name === 'get_user_id_by_email') {
        const email = args.user_email
        const q = query(collection(db, 'user_profiles'), where('email', '==', email))
        const snap = await getDocs(q)
        if (!snap.empty) {
          return { data: [{ user_id: snap.docs[0].id }], error: null }
        }
        return { data: [], error: null }
      }
      
      return { data: [], error: new Error(`RPC ${name} not implemented`) }
    } catch (error) {
      return { data: null, error }
    }
  },
  
  storage: {
    from: (bucketName) => {
      return {
        upload: async (path, file) => {
          try {
            // Convert file to Base64 data URL
            const base64Data = await fileToBase64(file)
            // Cache it under the path key
            logoBase64Cache.set(path, base64Data)
            return { data: { path }, error: null }
          } catch (error) {
            console.error('Base64 upload failed:', error)
            return { data: null, error }
          }
        },
        getPublicUrl: (path) => {
          const publicUrl = logoBase64Cache.get(path) || ''
          return { data: { publicUrl } }
        }
      }
    }
  }
}